import React, { useEffect, useState } from 'react';
import { Html5QrcodeScanner } from 'html5-qrcode';
import { useNavigate } from 'react-router-dom';
import PageContainer from '../components/layout/PageContainer';
import { motion } from 'framer-motion';
import { ArrowLeftIcon } from '@heroicons/react/24/outline';

const ScanQR: React.FC = () => {
  const navigate = useNavigate();
  const [scanResult, setScanResult] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [isScanning, setIsScanning] = useState(false);

  useEffect(() => {
    let scanner: Html5QrcodeScanner | null = null;

    const startScanner = async () => {
      try {
        // Request camera permission
        const stream = await navigator.mediaDevices.getUserMedia({ video: true });
        stream.getTracks().forEach(track => track.stop()); // Stop the stream after getting permission

        scanner = new Html5QrcodeScanner(
          'reader',
          {
            fps: 10,
            qrbox: { width: 250, height: 250 },
            aspectRatio: 1.0,
          },
          false
        );

        scanner.render(
          (decodedText) => {
            // Success callback
            setScanResult(decodedText);
            setIsScanning(false);
            if (scanner) {
              scanner.clear();
            }
          },
          (errorMessage) => {
            // Error callback
            console.warn(`QR Code scan error: ${errorMessage}`);
          }
        );

        setIsScanning(true);
      } catch (err) {
        setError('Não foi possível acessar a câmera. Por favor, verifique as permissões.');
        console.error('Camera permission error:', err);
      }
    };

    startScanner();

    // Cleanup function
    return () => {
      if (scanner) {
        scanner.clear();
      }
    };
  }, []);

  return (
    <PageContainer>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center gap-4">
          <motion.button
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
            onClick={() => navigate(-1)}
            className="p-2 rounded-full bg-white/10 hover:bg-white/20 transition-colors"
          >
            <ArrowLeftIcon className="h-6 w-6 text-white" />
          </motion.button>
          <h1 className="text-2xl font-bold text-white">Escanear QR Code</h1>
        </div>

        {/* Scanner Container */}
        <div className="relative">
          <div id="reader" className="w-full rounded-xl overflow-hidden" />
          
          {/* Overlay with instructions */}
          <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
            <div className="w-64 h-64 border-2 border-primary rounded-xl" />
          </div>
        </div>

        {/* Status Messages */}
        {error && (
          <div className="bg-red-500/20 text-red-400 p-4 rounded-xl">
            {error}
          </div>
        )}

        {scanResult && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-white/10 backdrop-blur-lg p-4 rounded-xl"
          >
            <h2 className="text-white font-medium mb-2">QR Code Detectado:</h2>
            <p className="text-white/80 break-all">{scanResult}</p>
          </motion.div>
        )}

        {/* Instructions */}
        <div className="text-white/60 text-center">
          <p>Posicione o QR code dentro da área de leitura</p>
          <p className="text-sm mt-1">A leitura é automática</p>
        </div>
      </div>
    </PageContainer>
  );
};

export default ScanQR; 